import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Lock, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PaymentPageStripe = () => {
  const [processing, setProcessing] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [plan, setPlan] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Get plan from location state or sessionStorage
    let planData = location.state?.plan;
    
    if (!planData) {
      // Try to get from sessionStorage
      const storedPlan = sessionStorage.getItem('paymentPlan');
      if (storedPlan) {
        try {
          planData = JSON.parse(storedPlan);
        } catch (e) {
          console.error('Failed to parse stored plan');
        }
      }
    } else {
      // Store in sessionStorage for page refreshes
      sessionStorage.setItem('paymentPlan', JSON.stringify(planData));
    }
    
    if (!planData) {
      // No plan data available, redirect to pricing
      navigate('/pricing');
      return;
    }
    
    setPlan(planData);
    
    // Load Stripe.js
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => setStripeLoaded(true);
    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [location, navigate]);

  const handlePayment = async () => {
    if (!stripeLoaded) {
      toast({
        title: "Loading",
        description: "Payment system is loading. Please wait...",
        variant: "default",
      });
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please login to continue",
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      // Extract price from plan (e.g., "$9.99" -> 9.99)
      const priceMatch = plan.price.match(/[\d.]+/);
      const price = priceMatch ? parseFloat(priceMatch[0]) : 9.99;

      // Check if this is a donation (from location state or sessionStorage)
      const isDonation = location.state?.isDonation || location.state?.type === 'donation' || false;

      // Create checkout session
      const response = await axios.post(
        `${API_URL}/api/stripe/create-checkout-session`,
        {
          plan_name: plan.name || 'Premium',
          price: price,
          is_donation: isDonation  // Pass donation flag to backend
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Payment Failed",
        description: error.response?.data?.detail || "Unable to process payment. Please try again.",
        variant: "destructive",
      });
      setProcessing(false);
    }
  };

  if (!plan) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="w-full px-2">
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center text-gray-600 hover:text-navy-500 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Pricing
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{plan.name} Plan</h3>
                  <p className="text-sm text-gray-600">{plan.duration || 'Monthly Subscription'}</p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold mb-2">Features Included:</h4>
                  <ul className="space-y-2">
                    {plan.features?.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-navy-500">{plan.price}/month</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Billed monthly. Cancel anytime.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center mb-6">
                <CreditCard className="w-6 h-6 text-navy-500 mr-2" />
                <h2 className="text-2xl font-bold">Secure Payment</h2>
              </div>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <Lock className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Secure Checkout</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Your payment is processed securely through Stripe. We never store your card details.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <div className="text-center">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click below to proceed to</p>
                    <p className="text-sm font-semibold text-gray-900">Stripe Secure Checkout</p>
                  </div>
                </div>

                <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                  <Lock className="w-3 h-3" />
                  <span>Secured by Stripe • PCI DSS Compliant</span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={processing || !stripeLoaded}
                className="w-full bg-mint-500 hover:bg-mint-600 text-white py-6 text-lg"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : !stripeLoaded ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Proceed to Secure Checkout
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500 mt-4">
                By continuing, you agree to our Terms of Service and Privacy Policy.
                Your subscription will auto-renew monthly until cancelled.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-gray-400">
            <div className="flex items-center">
              <Lock className="w-4 h-4 mr-1" />
              <span className="text-xs">SSL Encrypted</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span className="text-xs">100% Secure</span>
            </div>
            <div className="flex items-center">
              <CreditCard className="w-4 h-4 mr-1" />
              <span className="text-xs">PCI Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPageStripe;
